import { HttpClient } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import {
  HashMap,
  Translation,
  TRANSLOCO_CONFIG,
  TRANSLOCO_LOADER,
  translocoConfig,
  TranslocoLoader,
  TranslocoModule,
} from '@ngneat/transloco';
import { TranslocoLocaleModule } from '@ngneat/transloco-locale';
import { TranslocoMessageFormatModule } from '@ngneat/transloco-messageformat';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { toApiLocale } from '@lobos/library';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  /**
   * Per-request cache of in-flight / completed translation loads, keyed by the requested `lang`
   * (e.g. `core/cs`). Deduplicates concurrent identical loads within a single (SSR) render so a
   * burst of components sharing a scope does not trigger one backend call each.
   */
  private readonly cache = new Map<string, Observable<HashMap<any>>>();

  constructor(private http: HttpClient) {}

  getTranslation(lang: string): Observable<HashMap<any>> {
    if (environment.availableLang.includes(lang)) {
      return of({});
    }

    const cached = this.cache.get(lang);
    if (cached) {
      return cached;
    }

    const request$ = this.fetchTranslation(lang).pipe(
      // A failed translation load must never propagate: an unhandled error keeps Transloco from
      // marking the scope as loaded, so it re-requests on every change-detection tick — which
      // spirals into an infinite request storm during SSR (thousands of 403s/min against the API).
      // Falling back to empty translations lets the fallback language render and breaks the loop.
      catchError(() => of<HashMap<any>>({})),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.cache.set(lang, request$);

    return request$;
  }

  private fetchTranslation(lang: string): Observable<Translation> {
    if (environment.loadTranslationFromServer) {
      const langCode = lang.split('/').pop()!;
      const locale = lang.replace(langCode, toApiLocale(langCode));
      return this.http.get<Translation>(`${environment.host}${environment.apiUrl}/translation/${locale}`);
    }
    return this.http.get<Translation>(`/assets/i18n/${lang}.json`);
  }
}

@NgModule({
  imports: [TranslocoLocaleModule.forRoot(), TranslocoMessageFormatModule.forRoot()],
  exports: [TranslocoModule],
  providers: [
    {
      provide: TRANSLOCO_CONFIG,
      useValue: translocoConfig({
        availableLangs: environment.availableLang,
        defaultLang: environment.defaultLanguage,
        fallbackLang: environment.defaultLanguage,
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: environment.production,
        missingHandler: { allowEmpty: true },
      }),
    },
    { provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader },
  ],
})
export class TranslocoRootModule {}
